import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useSelector, shallowEqual } from "react-redux";

const AnalyticsBreadcrumb = ({
  currentComponent,
  customSurveyName,
  groupTitle,
  enableHistoryCrumb,
  datasetId: datasetIdProp,
  isHistoryView,
  historySurveyName,
  onNavigateToAnalytics,
  selectedItem,
  parentItem,
  onBackToOverview,
  onBackToParent,
}) => {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const sentinelRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  // Get dataset from either path params or query params
  const dataset = datasetIdProp || datasetId || searchParams.get('dataset');
  
  // Check if we're on a simple analytics route (like /leader/audit/simple/dashboard)
  const isOnSimpleAnalyticsRoute = location.pathname.includes('/leader/audit/simple/');
  
  // Check if we're on a pulse survey route (Leader)
  const isOnPulseSurveyRoute = location.pathname.includes(
    "/leader/audit/schedule-v2/pulse/"
  );
  // Check if we're on an OTP pulse survey route (App OTP)
  const isOnOtpPulseSurveyRoute = location.pathname.includes('/app/otp/AI/pulse-survey');
  
  // Check if we're specifically on a pulse survey view route
  const isOnPulseSurveyViewRoute = location.pathname.match(/^\/leader\/audit\/AI\/pulse\/view\/\d+$/);
  
  const { raw_data, get_survey_questions, currentUserGroupName, currentUserGroupCount } = useSelector(
    (state) => ({
      raw_data: state.audit?.raw_data,
      get_survey_questions: state.surveyquestions?.survey_questions,
      currentUserGroupName: state.current_user_group?.name || null,
      currentUserGroupCount: state.current_user_group?.memberCount ?? null,
    }),
    shallowEqual
  );

  // Map of component routes to display names
  const componentNames = {
    'dashboard': 'Dashboard',
    'factor': 'Outcome and Category Analytics',
    'heatmap': 'Heatmapping',
    'question': 'Question Distributions',
    'correlations': 'Category Correlations',
    'benchmarks': 'Benchmarking',
    'change': 'Change Analysis',
    'feedback': 'Comments',
    'insights': 'AI Insights',
    'copilot': 'Copilot',
    'copilot-nora': 'Nora AI',
    'copilot-risk': 'Risk Analysis',
    'copilot-roi': 'ROI Analysis',
    'copilot-thematic': 'Thematic Analysis',
    'copilot-change': 'Change Analysis',
    'relationships': 'ROI Insights',
    'kindred': 'Kindred Analysis',
    'custom': 'Custom Analytics',
    'completion-timeline': 'Completion Timeline',
    'question-analytics': 'Question Analytics',
  };

  // Get group title from metadata if not provided as prop
  const getGroupTitle = () => {
    // Only show group title when we're on a dashboard or analysis page, not on the main analytics table
    const isOnMainAnalyticsPage = location.pathname === '/leader/audit/new-analytics';
    if (isOnMainAnalyticsPage) {
      return null;
    }
    
    if (groupTitle) return groupTitle;
    
    // Check metadata for group title
    const metadataGroupTitle = raw_data?.[0]?.datasetMetadata?.groupTitle;
    if (metadataGroupTitle) {
      return metadataGroupTitle;
    }
    
    return null;
  };

  // Generate survey name with priority: custom > metadata > survey questions > fallback
  const getSurveyName = () => {
    if (customSurveyName) return customSurveyName;
    
    // Check custom data metadata first
    const customDataSurveyName = raw_data?.[0]?.datasetMetadata?.surveyName;
    if (customDataSurveyName) {
      return customDataSurveyName;
    }
    
    // Check if there's a title in the custom question data
    const customQuestion = raw_data?.[0]?.question;
    if (customQuestion?.title) {
      return customQuestion.title;
    }
    
    // Try to get from survey questions using sort order
    if (dataset && get_survey_questions?.length > 0) {
      const parts = dataset.split('-');
      if (parts.length >= 2 && parts[0] === 'sort') {
        const sortOrder = parseInt(parts[1]);
        const survey = get_survey_questions.find(q => q.sort_order === sortOrder);
        if (survey?.questions?.title) {
          return survey.questions.title;
        }
      }
    }
    
    return dataset ? "Survey Data" : "No Survey Selected";
  };

  // Calculate respondent count from the currently selected survey's data
  const getRespondentCount = () => {
    // Only calculate when we have survey data loaded (not on main analytics table)
    const isOnMainAnalyticsPage = location.pathname === '/leader/audit/new-analytics';
    if (isOnMainAnalyticsPage) {
      return null;
    }

    // Get responses from raw_data[0] which contains the selected survey's data
    const responses = raw_data?.[0]?.responses;
    if (Array.isArray(responses) && responses.length > 0) {
      return responses.length;
    }

    // Fallback: try to get from metadata if available
    const metadataCount = raw_data?.[0]?.datasetMetadata?.originalFilteredCount;
    if (metadataCount != null) {
      return metadataCount;
    }

    return null;
  };

  const handleNavigateToAnalytics = () => {
    if (typeof onNavigateToAnalytics === 'function') {
      onNavigateToAnalytics();
    }
    navigate('/leader/audit/new-analytics');
  };

  const handleNavigateToDashboard = () => {
    if (!dataset) return;
    navigate(`/leader/audit/new-analytics/dashboard/${dataset}`);
  };

  // Handle pulse survey navigation
  const handleNavigateToPulseSurveys = () => {
    navigate('/leader/audit/new-analytics?tab=pulse');
  };

  // Handle OTP pulse survey navigation
  const handleNavigateToOtpPulseRoot = () => {
    navigate('/app/otp/AI/pulse-survey');
  };

  // Toggle history via breadcrumb for Copilot
  const openHistory = () => {
    const next = new URLSearchParams(searchParams);
    next.set('history', '1');
    setSearchParams(next, { replace: false });
  };

  const stickyTopOffset = isOnOtpPulseSurveyRoute ? 8 : 0;

  useEffect(() => {
    if (!sentinelRef.current || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        rootMargin: `-${stickyTopOffset}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [stickyTopOffset]);

  const groupTitleValue = getGroupTitle();
  const surveyName = getSurveyName();
  const combinedLabel = groupTitleValue && surveyName
    ? `${groupTitleValue} - ${surveyName}`
    : surveyName;
  const historyLabel = historySurveyName || surveyName;

  const wrapperProps = isOnOtpPulseSurveyRoute ? { otp: true } : {};

  let breadcrumbTrail = null;

  // Get respondent count for the currently selected survey
  const respondentCount = getRespondentCount();

  // Use respondent count from selected survey if available, otherwise fall back to group count
  const displayCount = respondentCount != null ? respondentCount : currentUserGroupCount;

  const groupBadge = currentUserGroupName
    ? `${currentUserGroupName}${displayCount != null ? ` (${displayCount})` : ""}`
    : null;

  if (isOnOtpPulseSurveyRoute) {
    breadcrumbTrail = (
      <>
        <BreadcrumbItem onClick={handleNavigateToOtpPulseRoot} clickable>
          Pulse Surveys
        </BreadcrumbItem>
        <Separator>/</Separator>
        <BreadcrumbItem active>
          Pulse Survey Analytics
        </BreadcrumbItem>
      </>
    );
  } else if (isOnPulseSurveyRoute) {
    breadcrumbTrail = (
      <>
        <BreadcrumbItem onClick={handleNavigateToAnalytics} clickable>
          Survey Analytics
        </BreadcrumbItem>
        <Separator>/</Separator>
        <BreadcrumbItem onClick={handleNavigateToPulseSurveys} clickable>
          Pulse Surveys
        </BreadcrumbItem>
        <Separator>/</Separator>
        <BreadcrumbItem active>
          {isOnPulseSurveyViewRoute ? 'Pulse Survey Analytics' : 'Pulse Surveys'}
        </BreadcrumbItem>
      </>
    );
  } else if (isHistoryView) {
    breadcrumbTrail = (
      <>
        <BreadcrumbItem onClick={handleNavigateToAnalytics} clickable>
          Survey Analytics
        </BreadcrumbItem>
        {historyLabel && (
          <>
            <Separator>/</Separator>
            <BreadcrumbItem active>
              {`${historyLabel} - Series History`}
            </BreadcrumbItem>
          </>
        )}
      </>
    );
  } else {
    breadcrumbTrail = (
      <>
        <BreadcrumbItem onClick={handleNavigateToAnalytics} clickable>
          Survey Analytics
        </BreadcrumbItem>

        {dataset && combinedLabel && (
          <>
            <Separator>/</Separator>
            <BreadcrumbItem
              onClick={handleNavigateToDashboard}
              clickable={true}
              active={!currentComponent}
            >
              {combinedLabel}
            </BreadcrumbItem>
          </>
        )}

        {currentComponent && (
          <>
            <Separator>/</Separator>
            <BreadcrumbItem
              active={!selectedItem && !parentItem}
              clickable={(selectedItem || parentItem) && onBackToOverview}
              onClick={(selectedItem || parentItem) && onBackToOverview ? onBackToOverview : undefined}
            >
              {componentNames[currentComponent] || currentComponent}
            </BreadcrumbItem>
            {parentItem && (
              <>
                <Separator>/</Separator>
                <BreadcrumbItem
                  active={!selectedItem || selectedItem.questionCount !== undefined}
                  clickable={selectedItem && selectedItem.questionCount === undefined && onBackToParent}
                  onClick={selectedItem && selectedItem.questionCount === undefined && onBackToParent ? onBackToParent : undefined}
                >
                  {parentItem.title}
                </BreadcrumbItem>
              </>
            )}
            {selectedItem && selectedItem.questionCount !== undefined && !parentItem && (
              <>
                <Separator>/</Separator>
                <BreadcrumbItem active>
                  {selectedItem.title}
                </BreadcrumbItem>
              </>
            )}
            {selectedItem && selectedItem.questionCount === undefined && parentItem && (
              <>
                <Separator>/</Separator>
                <BreadcrumbItem active>
                  {selectedItem.title && selectedItem.title.length > 50
                    ? `${selectedItem.title.substring(0, 30)}...`
                    : selectedItem.title}
                </BreadcrumbItem>
              </>
            )}
            {enableHistoryCrumb && currentComponent.startsWith('copilot') && (
              <>
                <Separator>/</Separator>
                <BreadcrumbItem clickable onClick={openHistory}>History</BreadcrumbItem>
              </>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <StickySentinel ref={sentinelRef} aria-hidden="true" />
      <BreadcrumbWrapper {...wrapperProps}>
        <BreadcrumbBackdrop isSticky={isSticky} />
        <BreadcrumbContainer>
          <BreadcrumbContent>
            <CrumbTrail>{breadcrumbTrail}</CrumbTrail>
            {groupBadge && (
              <GroupMeta title="Your group">
                <span>{groupBadge}</span>
                <InfoWrapper tabIndex="0" aria-label="Group roll-up info">
                  <InfoIcon aria-hidden="true">i</InfoIcon>
                  <Tooltip role="tooltip">
                    This is the total number of respondents included in your group’s roll-up.
                  </Tooltip>
                </InfoWrapper>
              </GroupMeta>
            )}
          </BreadcrumbContent>
        </BreadcrumbContainer>
      </BreadcrumbWrapper>
    </>
  );
};

const BreadcrumbWrapper = styled.div`
  position: sticky;
  top: ${(props) => (props.otp ? '8px' : '0')};
  z-index: ${(props) => (props.otp ? 1200 : 300)}; /* Ensure above app top bar when OTP */
  margin-bottom: 24px;
  margin-top: ${(props) => (props.otp ? '0' : '-14px')};
  max-width: 100%;
  background: transparent;
  display: block;
  overflow: visible;
`;

const BreadcrumbBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 100vw;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  transform: ${(props) => (props.isSticky ? 'translate(-50%, 0%)' : 'translate(-50%, -100%)')};
  opacity: ${(props) => (props.isSticky ? 1 : 0)};
  transition: transform 0.24s ease, opacity 0.24s ease, box-shadow 0.24s ease;
  box-shadow: ${(props) => (props.isSticky ? '0 12px 24px rgba(17, 24, 39, 0.08)' : 'none')};
  pointer-events: none;
  z-index: 0;
`;

const StickySentinel = styled.div`
  position: relative;
  width: 100%;
  height: 1px;
  margin-top: -1px;
  opacity: 0;
  pointer-events: none;
`;

const BreadcrumbContainer = styled.div`
  background: transparent;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
`;

const BreadcrumbContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6B7280;
`;

const CrumbTrail = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BreadcrumbItem = styled.span`
  color: ${props => props.active ? '#111827' : '#6B7280'};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.clickable ? '#374151' : props.active ? '#111827' : '#6B7280'};
  }
  
  ${props => props.clickable && `
    &:hover {
      text-decoration: underline;
      text-decoration-color: #D1D5DB;
      text-underline-offset: 3px;
    }
  `}
`;

const Separator = styled.span`
  margin: 0 8px;
  color: #D1D5DB;
  font-weight: 400;
  user-select: none;
`;

const GroupMeta = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const InfoWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  color: #4b5563;
  background: #fff;
  font-size: 12px;
  cursor: default;
  outline: none;

  &:hover > div,
  &:focus > div {
    opacity: 1;
    visibility: visible;
    transform: translate(0, -4px);
  }
`;

const InfoIcon = styled.span`
  line-height: 1;
  font-weight: 700;
`;

const Tooltip = styled.div`
  position: absolute;
  top: -2px;
  right: 0;
  left: auto;
  transform: translate(0, 4px);
  background: #111827;
  color: #f9fafb;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: normal;
  text-align: left;
  max-width: 360px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.08s ease, transform 0.08s ease;
  z-index: 2;
  pointer-events: none;
`;

export default AnalyticsBreadcrumb; 
